import { useCallback, useMemo, useState } from "react";
import { authApi } from "../../../api";

type ActionResult = { ok: true } | { ok: false; message?: string };

export type SendResetFn = (email: string) => Promise<ActionResult>;

export interface UseForgotPasswordArgs {
    sendResetFn?: SendResetFn;
    onSuccess?: (email: string) => void;
    initialEmail?: string | undefined;
}

export interface UseForgotPasswordReturn {
    email: string;
    setEmail: (v: string) => void;

    loading: boolean;
    error: string | null;

    submit: () => Promise<void>;
    resetError: () => void;
}

async function sendReset(email: string): Promise<ActionResult> {
    try {
        await authApi.forgotPassword(email);
        return { ok: true };
    } catch (e: unknown) {
        const message =
            (e as { response?: { data?: { detail?: string } } })?.response?.data
                ?.detail || "Failed to send reset code.";
        return { ok: false, message };
    }
}

export function useForgotPassword(
    args: UseForgotPasswordArgs,
): UseForgotPasswordReturn {
    const { sendResetFn, onSuccess, initialEmail } = args;

    const doSend = useMemo(() => sendResetFn ?? sendReset, [sendResetFn]);

    const [email, setEmail] = useState(initialEmail || "");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const resetError = useCallback(() => setError(null), []);

    const submit = useCallback(async () => {
        resetError();

        const trimmed = email.trim();
        if (!trimmed) {
            setError("Please enter your email.");
            return;
        }

        setLoading(true);
        try {
            const res = await doSend(trimmed);
            if (!res.ok) {
                setError(res.message ?? "Could not send reset code.");
                return;
            }
            onSuccess?.(trimmed);
        } catch (e) {
            setError(
                e instanceof Error ? e.message : "Could not send reset code.",
            );
        } finally {
            setLoading(false);
        }
    }, [doSend, email, onSuccess, resetError]);

    return { email, setEmail, loading, error, submit, resetError };
}
